<script lang="ts">
	import type { CrudStore } from '@isoftdata/svelte-store-crud'
	import type { i18n } from 'types/common'

	import { getContext } from 'svelte'
	import { v4 as uuid } from '@lukeed/uuid'
	import { hasPermission } from 'utility/permission'

	import {
		type Store,
		type Customer,
		type SalesPerson,
		type State,
		billDeliveryMethodOptions,
		type CustomerAlternateAddress,
	} from 'utility/customer-helper'

	import BillingAndAlternateAddress, {
		type BillingAddress,
		type AlternateAddress,
	} from 'components/BillingAndAlternateAddress.svelte'
	import Input from '@isoftdata/svelte-input'
	import Select from '@isoftdata/svelte-select'
	import { getEventValue } from '@isoftdata/browser-event'

	interface Props {
		customer: Customer
		states?: Array<State>
		stores?: Array<Store>
		salesPersonList?: Array<SalesPerson>
		customerChanged: boolean
		viewOnlyMode?: boolean // the permission to edit a customer overall
		enableAddressValidation?: boolean
	}

	let {
		customer = $bindable(),
		states = [],
		stores = [],
		salesPersonList = [],
		customerChanged = $bindable(),
		viewOnlyMode = false,
		enableAddressValidation = false,
	}: Props = $props()

	let billingAddress: BillingAddress = $state(formatCustomerBillingAddress(customer))
	let selectedAlternateAddress: CustomerAlternateAddress = $state()!

	const alternateAddressCrudStore = getContext<CrudStore<CustomerAlternateAddress, 'uuid'>>('alternateAddressCrudStore')
	const { t: translate } = getContext<i18n>('i18next')

	let alternateAddresses: Array<AlternateAddress> = $state(formatCustomerAlternateAddress(customer.addresses))

	let customerCanEditBillingAddress = hasPermission('CUSTOMERS_CAN_EDIT_BILLING_ADDRESS', customer.store?.id)
	let customerCanEditAlternateAddress = hasPermission('CUSTOMERS_CAN_EDIT_ADDRESSES', customer.store?.id)

	function formatCustomerBillingAddress(customer: Customer): BillingAddress {
		return {
			companyName: customer.companyName,
			contactName: customer.contactName,
			street: customer.billingAddress.address1,
			city: customer.billingAddress.city,
			state: customer.billingAddress.state,
			zip: customer.billingAddress.zip,
			country: customer.billingAddress.country,
			email: customer.email,
			faxNumber: customer.faxNumber,
			phoneNumber: customer.phoneNumber,
			mobilePhoneNumber: customer.mobilePhoneNumber,
			validated: customer.billingAddressValidated,
		}
	}

	function formatCustomerAlternateAddress(addresses: Customer['addresses']): Array<AlternateAddress> {
		let formatedAlternateAddresses: Array<AlternateAddress> = addresses.map(address => {
			return {
				id: address.id,
				uuid: uuid(),
				label: address.label,
				primary: address.primary,
				addressData: {
					companyName: address.companyName,
					contactName: address.contactName,
					street: address.street,
					city: address.city,
					state: address.state,
					zip: address.zip,
					country: address.country,
					email: address.email,
					faxNumber: address.faxNumber,
					phoneNumber: address.phoneNumber,
					mobilePhoneNumber: address.mobilePhoneNumber,
					department: address.department,
					notes: address.notes,
					validated: !!address.lastValidated,
				},
			}
		})

		// TODO: maybe we need to optimize this logic later
		// The issue here with this fix is that, the customer.addresses object does not have all the changes
		// Due to that, if the user leave the page without saving, then come back to the customer page,
		// the changes made to the alternate address will be lost.
		if (alternateAddressCrudStore.createdValues.length) {
			alternateAddressCrudStore.createdValues.forEach(newAddress => {
				formatedAlternateAddresses.push({
					id: newAddress.id,
					uuid: newAddress.uuid,
					label: newAddress.label,
					primary: newAddress.primary,
					addressData: newAddress.addressData,
				})
			})
		}

		if (alternateAddressCrudStore.updatedValues.length) {
			alternateAddressCrudStore.updatedValues.forEach(updatedAddress => {
				const index = formatedAlternateAddresses.findIndex(addr => addr.id === updatedAddress.id)
				if (index !== -1) {
					formatedAlternateAddresses[index] = {
						id: updatedAddress.id,
						uuid: updatedAddress.uuid,
						label: updatedAddress.label,
						primary: updatedAddress.primary,
						addressData: updatedAddress.addressData,
					}
				}
			})
		}

		return formatedAlternateAddresses
	}

	function updateCustomerBillingAddress(updatedBillingAddress: BillingAddress) {
		customer.companyName = updatedBillingAddress.companyName
		customer.contactName = updatedBillingAddress.contactName
		customer.billingAddress = {
			...customer.billingAddress,
			address1: updatedBillingAddress.street,
			city: updatedBillingAddress.city,
			state: updatedBillingAddress.state,
			zip: updatedBillingAddress.zip,
			country: updatedBillingAddress.country,
		}
		customer.faxNumber = updatedBillingAddress.faxNumber
		customer.mobilePhoneNumber = updatedBillingAddress.mobilePhoneNumber
		customer.billingAddressValidated = updatedBillingAddress.validated
		customerChanged = true
	}

	const selectedCustomerAlternateAddress = $derived(
		customer.addresses.find(address => address.id === selectedAlternateAddress?.id) ?? null,
	)
</script>

<BillingAndAlternateAddress
	{states}
	{viewOnlyMode}
	{enableAddressValidation}
	{alternateAddressCrudStore}
	canEditBillingAddress={customerCanEditBillingAddress}
	canEditAlternateAddress={customerCanEditAlternateAddress}
	bind:billingAddress
	bind:alternateAddresses
	bind:selectedAlternateAddress
	bind:hasChanges={customerChanged}
	billingAddressChange={billingAddress => updateCustomerBillingAddress(billingAddress)}
>
	{#snippet billingTopFields()}
		<div class="col-12 col-md-6">
			<Select
				required
				showEmptyOption={false}
				label={translate('customer.billDeliveryMethod', 'Bill Delivery Method')}
				disabled={viewOnlyMode || !customerCanEditBillingAddress}
				bind:value={customer.billDeliveryMethod}
				onchange={() => {
					customerChanged = true
				}}
			>
				{#each billDeliveryMethodOptions as option}
					<option value={option.value}>{option.label}</option>
				{/each}
			</Select>
		</div>
		<div class="col-12 col-md-6">
			<Input
				disabled
				label={translate('customer.lastBillDate', 'Last Bill Date')}
				value={customer.lastBillDate ? customer.lastBillDate.toLocaleString() : null}
			/>
		</div>
	{/snippet}
	{#snippet billingAdditionalFields()}
		<div class="col-12 col-md-6">
			<Input
				readonly
				label={translate('customer.storeRegion', 'Store Region')}
				value={customer.storeRegion ?? ''}
			/>
		</div>
		<div class="col-12 col-md-6">
			<Input
				readonly
				label={translate('customer.salesRegion', 'Sales Region')}
				value={customer.salesRegion ?? ''}
			/>
		</div>
		<div class="col-12 col-md-6">
			<Select
				required
				showEmptyOption={false}
				label={translate('common:store', 'Store')}
				value={customer.store?.id ?? null}
				disabled={viewOnlyMode || !customerCanEditBillingAddress}
				onchange={event => {
					const value = getEventValue(event)
					if (value) {
						const id = parseInt(value, 10)
						customer.store = stores.find(store => store.id === id) ?? null
					}
					customerChanged = true
				}}
			>
				{#each stores as store}
					<option value={store.id}>{store.name}</option>
				{/each}
			</Select>
		</div>
		<div class="col-12 col-md-6">
			<Select
				required
				showEmptyOption={false}
				label={translate('common:salesperson', 'Salesperson')}
				value={customer.salesPerson?.id ?? null}
				disabled={viewOnlyMode || !customerCanEditBillingAddress}
				onchange={event => {
					const value = getEventValue(event)
					if (value) {
						const id = parseInt(value, 10)
						customer.salesPerson = salesPersonList.find(salesPerson => salesPerson.id === id) ?? null
					}
					customerChanged = true
				}}
			>
				{#each salesPersonList as salesPerson}
					<option value={salesPerson.id}>{salesPerson.name}</option>
				{/each}
			</Select>
		</div>
	{/snippet}
	{#snippet alternateAdditionalFields()}
		<div class="col-12 col-md-6">
			<Select
				showEmptyOption
				label={translate('common:store', 'Store')}
				emptyText={translate('customer.useBillingDefault', 'Use Billing Default')}
				value={selectedCustomerAlternateAddress?.store?.id ?? null}
				disabled={viewOnlyMode || !customerCanEditAlternateAddress}
				onchange={event => {
					const value = getEventValue(event)
					if (value && selectedCustomerAlternateAddress) {
						const id = parseInt(value, 10)
						selectedCustomerAlternateAddress.store = stores.find(store => store.id === id) ?? null
						const alternateAddressForUpdate = {
							...selectedAlternateAddress,
							storeId: selectedCustomerAlternateAddress.store?.id ?? null,
							salesPersonId: selectedCustomerAlternateAddress.salesPerson?.id ?? null,
						}
						alternateAddressCrudStore.update(alternateAddressForUpdate)
					}
					customerChanged = true
				}}
			>
				{#each stores as store}
					<option value={store.id}>{store.name}</option>
				{/each}
			</Select>
		</div>
		<div class="col-12 col-md-6">
			<Select
				showEmptyOption
				label={translate('common:salesperson', 'Salesperson')}
				emptyText={translate('customer.useBillingDefault', 'Use Billing Default')}
				value={selectedCustomerAlternateAddress?.salesPerson?.id ?? null}
				disabled={viewOnlyMode || !customerCanEditAlternateAddress}
				onchange={event => {
					const value = getEventValue(event)
					if (value && selectedCustomerAlternateAddress) {
						const id = parseInt(value, 10)
						selectedCustomerAlternateAddress.salesPerson =
							salesPersonList.find(salesPerson => salesPerson.id === id) ?? null
						const alternateAddressForUpdate = {
							...selectedAlternateAddress,
							storeId: selectedCustomerAlternateAddress.store?.id ?? null,
							salesPersonId: selectedCustomerAlternateAddress.salesPerson?.id ?? null,
						}
						alternateAddressCrudStore.update(alternateAddressForUpdate)
					}
					customerChanged = true
				}}
			>
				{#each salesPersonList as salesPerson}
					<option value={salesPerson.id}>{salesPerson.name}</option>
				{/each}
			</Select>
		</div>
	{/snippet}
</BillingAndAlternateAddress>
